<template>
  <div class="scanner-page">
    <Toast />
    <main class="scanner-panel">
      <div class="scanner-header">
        <i :class="socketConectado ? 'pi pi-wifi' : 'pi pi-qrcode'"></i>
        <div>
          <h1>Escáner compartido</h1>
          <p>{{ estadoConexion }}</p>
        </div>
      </div>

      <div v-if="socketError" class="connection-alert">
        <strong>No se pudo conectar con la computadora.</strong>
        <span>Revisa que el puerto WebSocket esté publicado y que la URL esté configurada.</span>
        <Button label="Reintentar" icon="pi pi-refresh" class="p-button-sm p-button-warning mt-2" @click="conectarWebSocket" />
      </div>

      <div class="scanner-box">
        <video ref="scannerVideo" class="scanner-video" muted playsinline></video>
        <div class="scanner-frame"></div>
        <div class="scanner-status">{{ scannerMensaje }}</div>
      </div>

      <div class="scanner-actions">
        <Button
          :label="scannerActivo ? 'Detener cámara' : 'Activar cámara'"
          :icon="scannerActivo ? 'pi pi-stop' : 'pi pi-camera'"
          :class="scannerActivo ? 'p-button-danger' : 'p-button-info'"
          class="w-full"
          @click="scannerActivo ? detenerEscaner() : abrirEscaner()"
        />
      </div>

      <div class="manual-box">
        <span><strong>CÓDIGO DE BARRA MANUAL:</strong></span>
        <div class="p-inputgroup mt-1">
          <InputText v-model="codigoManual" placeholder="Número del código de barra del producto" @keyup.enter="enviarManual" />
          <Button icon="pi pi-send" class="p-button-success" :disabled="!codigoManual" @click="enviarManual" />
        </div>
      </div>

      <div v-if="ultimoCodigo" class="last-code">
        Último enviado: <strong>{{ ultimoCodigo }}</strong>
      </div>
    </main>
  </div>
</template>

<script>
import SharedBarcodeScannerService from '@/service/SharedBarcodeScannerService'

export default {
  name: 'ScannerCompartido',
  data() {
    return {
      service: new SharedBarcodeScannerService(),
      sessionId: this.$route.params.sessionId,
      codigoManual: '',
      ultimoCodigo: '',
      scannerMensaje: 'Activa la cámara para escanear',
      scannerDetector: null,
      scannerStream: null,
      scannerFrame: null,
      scannerActivo: false,
      enviando: false,
      ultimoCodigoDetectado: '',
      scannerLecturaPendiente: '',
      scannerLecturaConteo: 0,
      scannerLecturaTiempo: 0,
      scannerConfirmaciones: 3,
      scannerFormatos: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e'],
      socket: null,
      socketConectado: false,
      socketError: false,
      socketTimeout: null
    }
  },
  computed: {
    estadoConexion() {
      if (this.socketConectado) return 'Conectado a la computadora.'
      if (this.socketError) return 'Sin conexión WebSocket.'
      return 'Conectando con la computadora...'
    }
  },
  mounted() {
    this.conectarWebSocket()
    this.abrirEscaner()
  },
  beforeUnmount() {
    this.detenerEscaner()
    this.desconectarWebSocket()
  },
  methods: {
    conectarWebSocket() {
      this.desconectarWebSocket()
      const socket = new WebSocket(this.service.websocketUrl(this.sessionId, 'phone'))

      this.socket = socket
      this.socketError = false
      this.socketConectado = false
      this.scannerMensaje = 'Conectando con la computadora...'
      this.socketTimeout = setTimeout(() => {
        if (!this.socketConectado) {
          this.socketError = true
          this.scannerMensaje = 'No se pudo conectar con la computadora'
        }
      }, 6000)

      socket.onopen = () => {
        clearTimeout(this.socketTimeout)
        this.socketConectado = true
        this.socketError = false
        this.scannerMensaje = 'Apunta la cámara al código'
      }

      socket.onmessage = (event) => {
        let payload = {}

        try {
          payload = JSON.parse(event.data)
        } catch (error) {
          console.error(error)
          return
        }

        if (payload.type === 'barcode_sent') {
          this.ultimoCodigo = payload.code
          this.scannerMensaje = `Enviado: ${payload.code}`
        }
      }

      socket.onerror = () => {
        clearTimeout(this.socketTimeout)
        this.socketError = true
        this.scannerMensaje = 'No se pudo conectar con la computadora'
      }

      socket.onclose = () => {
        clearTimeout(this.socketTimeout)
        this.socketConectado = false
        if (!this.socketError) {
          this.scannerMensaje = 'Conexión cerrada'
        }
      }
    },
    desconectarWebSocket() {
      if (this.socketTimeout) {
        clearTimeout(this.socketTimeout)
        this.socketTimeout = null
      }
      if (this.socket) {
        this.socket.close()
        this.socket = null
      }
    },
    async abrirEscaner() {
      if (!('BarcodeDetector' in window)) {
        this.scannerMensaje = 'Este navegador no soporta escaneo automático. Usa el campo manual.'
        this.$toast.add({
          severity: 'warn',
          summary: 'Escáner',
          detail: 'Prueba con Chrome en Android o usa el campo manual.',
          life: 4000
        })
        return
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        this.scannerMensaje = 'La cámara requiere HTTPS.'
        this.$toast.add({
          severity: 'warn',
          summary: 'Cámara',
          detail: 'Abre esta página con HTTPS para permitir la cámara.',
          life: 4000
        })
        return
      }

      try {
        this.scannerDetector = new window.BarcodeDetector({
          formats: this.scannerFormatos
        })
        this.scannerStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        })

        const video = this.$refs.scannerVideo
        video.srcObject = this.scannerStream
        await video.play()
        this.scannerActivo = true
        this.scannerMensaje = 'Apunta la cámara al código'
        this.detectarCodigo()
      } catch (error) {
        console.error(error)
        this.scannerMensaje = 'No se pudo abrir la cámara'
        this.detenerEscaner()
      }
    },
    detenerEscaner() {
      this.scannerActivo = false
      this.resetearConfirmacionScanner()
      if (this.scannerFrame) {
        cancelAnimationFrame(this.scannerFrame)
        this.scannerFrame = null
      }
      if (this.scannerStream) {
        this.scannerStream.getTracks().forEach((track) => track.stop())
        this.scannerStream = null
      }
      if (this.$refs.scannerVideo) {
        this.$refs.scannerVideo.srcObject = null
      }
    },
    async detectarCodigo() {
      if (!this.scannerActivo || !this.scannerDetector || !this.$refs.scannerVideo) return

      try {
        const codigos = await this.scannerDetector.detect(this.$refs.scannerVideo)
        const codigo = this.confirmarCodigoEscaneado(this.seleccionarCodigoDetectado(codigos))

        if (codigo && codigo !== this.ultimoCodigoDetectado) {
          this.ultimoCodigoDetectado = codigo
          await this.enviarCodigo(codigo)
          setTimeout(() => {
            this.ultimoCodigoDetectado = ''
          }, 1500)
        }
      } catch (error) {
        console.error(error)
      }

      this.scannerFrame = requestAnimationFrame(this.detectarCodigo)
    },
    seleccionarCodigoDetectado(codigos) {
      const detecciones = (codigos || []).filter((codigo) => codigo?.rawValue)
      if (!detecciones.length) return ''

      const dentroMarco = detecciones.find((codigo) => this.codigoDentroDelMarco(codigo))
      return this.normalizarCodigoEscaneado((dentroMarco || detecciones[0]).rawValue)
    },
    codigoDentroDelMarco(codigo) {
      const video = this.$refs.scannerVideo
      const box = codigo?.boundingBox
      const width = video?.videoWidth || video?.clientWidth || 0
      const height = video?.videoHeight || video?.clientHeight || 0

      if (!box || !width || !height) return true

      const centerX = box.x + box.width / 2
      const centerY = box.y + box.height / 2

      return centerX >= width * 0.1 && centerX <= width * 0.9 && centerY >= height * 0.34 && centerY <= height * 0.62
    },
    confirmarCodigoEscaneado(codigo) {
      if (!codigo) return ''

      const ahora = Date.now()
      if (codigo === this.scannerLecturaPendiente && ahora - this.scannerLecturaTiempo < 1200) {
        this.scannerLecturaConteo += 1
      } else {
        this.scannerLecturaPendiente = codigo
        this.scannerLecturaConteo = 1
      }
      this.scannerLecturaTiempo = ahora

      if (this.scannerLecturaConteo < this.scannerConfirmaciones) {
        this.scannerMensaje = `Confirmando: ${codigo} (${this.scannerLecturaConteo}/${this.scannerConfirmaciones})`
        return ''
      }

      this.resetearConfirmacionScanner()
      return codigo
    },
    resetearConfirmacionScanner() {
      this.scannerLecturaPendiente = ''
      this.scannerLecturaConteo = 0
      this.scannerLecturaTiempo = 0
    },
    normalizarCodigoEscaneado(texto) {
      return String(texto ?? '')
        .trim()
        .toUpperCase()
        .replace(/['`´]/g, '-')
    },
    enviarManual() {
      if (!this.codigoManual) return
      this.enviarCodigo(this.codigoManual)
      this.codigoManual = ''
    },
    async enviarCodigo(codigo) {
      if (this.enviando) return
      if (!this.socketConectado || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
        this.scannerMensaje = 'El teléfono no está conectado con la computadora'
        this.$toast.add({
          severity: 'warn',
          summary: 'Sin conexión',
          detail: 'Vuelve a escanear el QR desde la computadora.',
          life: 3000
        })
        return
      }

      this.enviando = true
      const codigoLimpio = this.normalizarCodigoEscaneado(codigo)

      try {
        this.socket.send(
          JSON.stringify({
            type: 'barcode',
            code: codigoLimpio
          })
        )
        this.ultimoCodigo = codigoLimpio
        this.scannerMensaje = `Enviado: ${codigoLimpio}`
        this.$toast.add({
          severity: 'success',
          summary: 'Enviado',
          detail: codigoLimpio,
          life: 1200
        })
      } catch (error) {
        console.error(error)
        this.scannerMensaje = 'No se pudo enviar. Revisa la conexión.'
      } finally {
        this.enviando = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.scanner-page {
  min-height: 100vh;
  padding: 18px;
  background: #e5edf5;
  display: flex;
  align-items: center;
  justify-content: center;
}
.scanner-panel {
  width: min(100%, 430px);
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 14px;
}
.scanner-header {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
  color: #0f172a;
}
.scanner-header i {
  font-size: 1.7rem;
  color: #2563eb;
}
.scanner-header h1 {
  margin: 0;
  font-size: 1.25rem;
}
.scanner-header p {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 0.9rem;
}
.connection-alert {
  margin-bottom: 12px;
  padding: 10px;
  border-radius: 6px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.connection-alert span {
  font-size: 0.85rem;
}
.scanner-box {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: #0f172a;
  aspect-ratio: 3 / 4;
}
.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.scanner-frame {
  position: absolute;
  left: 10%;
  right: 10%;
  top: 34%;
  height: 28%;
  border: 2px solid #22c55e;
  border-radius: 8px;
  box-shadow: 0 0 0 999px rgba(15, 23, 42, 0.45);
}
.scanner-frame::after {
  content: '';
  position: absolute;
  left: 8px;
  right: 8px;
  top: 50%;
  border-top: 2px solid #ef4444;
}
.scanner-status {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.85);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}
.scanner-actions {
  margin-top: 12px;
}
.manual-box {
  margin-top: 14px;
}
.last-code {
  margin-top: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #dcfce7;
  color: #166534;
  text-align: center;
}
</style>
